<template>
  <v-container fluid class="budget-report-page">
    <!-- Header + controls in one bar -->
    <header class="budget-report-header">
      <h1 class="budget-report-title">{{ $t('budgetReport.title') }}</h1>
      <div class="budget-report-controls">
        <v-btn-toggle
          v-model="selectedPeriod"
          mandatory
          color="primary"
          density="compact"
          variant="outlined"
          divided
          class="period-toggle"
        >
          <v-btn value="day" size="small">{{ $t('budgetReport.periodDay') }}</v-btn>
          <v-btn value="month" size="small">{{ $t('budgetReport.periodMonth') }}</v-btn>
          <v-btn value="year" size="small">{{ $t('budgetReport.periodYear') }}</v-btn>
        </v-btn-toggle>
        <template v-if="selectedPeriod === 'day'">
          <v-text-field
            v-model="dateFrom"
            :label="$t('budgetReport.dateFrom')"
            type="date"
            density="compact"
            hide-details
            variant="outlined"
            class="control-field"
            @change="loadReport"
          />
          <v-text-field
            v-model="dateTo"
            :label="$t('budgetReport.dateTo')"
            type="date"
            density="compact"
            hide-details
            variant="outlined"
            class="control-field"
            @change="loadReport"
          />
        </template>
        <v-text-field
          v-else-if="selectedPeriod === 'month'"
          v-model="selectedMonth"
          :label="$t('budgetReport.onMonth')"
          type="month"
          density="compact"
          hide-details
          variant="outlined"
          class="control-field"
          @change="loadReport"
        />
        <v-text-field
          v-else
          v-model.number="selectedYear"
          :label="$t('budgetReport.onYear')"
          type="number"
          density="compact"
          hide-details
          variant="outlined"
          class="control-field control-field-year"
          @change="loadReport"
        />
        <span class="period-description">{{ periodDescription }}</span>
      </div>
    </header>

    <!-- Report Table -->
    <div class="budget-report-table-wrapper">
      <table class="budget-report-table">
        <thead>
          <tr>
            <th class="sticky-col col-name">{{ $t('budgetReport.budgetLine') }}</th>
            <th
              v-for="col in columns"
              :key="col.date"
              class="col-value"
            >
              <span class="col-label">{{ col.label }}</span>
              <span class="col-date">{{ formatColDate(col.date) }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in flatRows"
            :key="row.id"
            :class="{
              'row-folder': row.is_folder,
              'row-branch-active': row.isActiveBranch,
              'row-branch-passive': !row.isActiveBranch
            }"
          >
            <td
              class="sticky-col col-name"
              :style="{ paddingLeft: (row.depth * 18 + 12) + 'px' }"
            >
              <v-icon size="small" class="row-icon">
                {{ row.is_folder ? 'mdi-folder-outline' : 'mdi-file-document-outline' }}
              </v-icon>
              <span :class="{ 'row-name-folder': row.is_folder }">{{ row.name }}</span>
            </td>
            <td
              v-for="(col, colIndex) in columns"
              :key="col.date"
              class="col-value cell-value-block"
            >
              <template v-if="getCellValue(row.id, col.date) !== null">
                <span class="cell-value">{{ formatNumber(getCellValue(row.id, col.date)) }}</span>
                <span
                  v-if="getCellDelta(row.id, colIndex) !== null && getCellDelta(row.id, colIndex) !== 0"
                  class="cell-delta"
                  :class="{ 'delta-negative': (getCellDelta(row.id, colIndex) ?? 0) < 0 }"
                >
                  {{ formatDelta(getCellDelta(row.id, colIndex)!) }}
                </span>
              </template>
            </td>
          </tr>
          <tr v-if="flatRows.length === 0 && !loading" class="row-empty">
            <td :colspan="columns.length + 1" class="empty-state">
              <v-icon size="48" icon="mdi-table-large" class="empty-icon" />
              <p class="empty-text">{{ $t('budgetReport.noData') }}</p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-overlay v-model="loading" contained class="align-center justify-center" scrim="rgba(255,255,255,0.7)">
      <v-progress-circular indeterminate size="48" width="3" />
    </v-overlay>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet } from '../utils/api'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

interface BudgetLine {
  id: number
  name: string
  parent_id: number | null
  order: number
  is_folder: boolean
  active: boolean
}

interface Column {
  label: string
  date: string
}

interface FlatRow extends BudgetLine {
  depth: number
  isActiveBranch: boolean
}

const now = new Date()
const today = now.toISOString().slice(0, 10)
const currentYear = now.getFullYear()

const selectedPeriod = ref<string>('month')
const dateFrom = ref(today)
const dateTo = ref(today)
const selectedMonth = ref(today.slice(0, 7))
const selectedYear = ref(currentYear)
const loading = ref(false)

function initFromQuery() {
  const q = route.query
  selectedPeriod.value = (q.period as string) || 'month'
  dateFrom.value = (q.date_from as string) || today
  dateTo.value = (q.date_to as string) || today
  selectedMonth.value = (q.month as string) || today.slice(0, 7)
  const y = parseInt(q.year as string, 10)
  selectedYear.value = Number.isFinite(y) ? y : currentYear
}

function updateUrlFromParams() {
  const query: Record<string, string> = { period: selectedPeriod.value }
  if (selectedPeriod.value === 'day') {
    query.date_from = dateFrom.value
    query.date_to = dateTo.value
  } else if (selectedPeriod.value === 'month') {
    query.month = selectedMonth.value
  } else {
    query.year = String(selectedYear.value)
  }
  router.replace({ path: route.path, query })
}

const budgetLines = ref<BudgetLine[]>([])
const columns = ref<Column[]>([])
const reportData = ref<Record<string, Record<string, number>>>({})
const activeLineId = ref<number>(5)

// Period description text
const periodDescription = computed(() => {
  if (selectedPeriod.value === 'day') {
    if (!dateFrom.value || !dateTo.value) return ''
    const from = new Date(dateFrom.value + 'T00:00:00')
    const to = new Date(dateTo.value + 'T00:00:00')
    const diffDays = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)) + 1
    return t('budgetReport.daysInRange', { days: diffDays })
  } else if (selectedPeriod.value === 'month') {
    if (!selectedMonth.value) return ''
    const d = new Date(selectedMonth.value + '-01T00:00:00')
    const monthName = d.toLocaleString('default', { month: 'long', year: 'numeric' })
    return t('budgetReport.weeksInMonth', { month: monthName })
  } else {
    return t('budgetReport.monthsInYear', { year: selectedYear.value })
  }
})

// Build flat rows with depth and active/passive branch from budget lines (recursive)
const flatRows = computed<FlatRow[]>(() => {
  const rows: FlatRow[] = []
  const sort = (a: BudgetLine, b: BudgetLine) => a.order - b.order || a.name.localeCompare(b.name)

  const walk = (parentId: number | null, depth: number, branchActive?: boolean) => {
    const children = budgetLines.value
      .filter(bl => bl.parent_id === parentId)
      .sort(sort)

    for (const child of children) {
      const isActiveBranch = depth === 0 ? child.active : (branchActive ?? child.active)
      rows.push({ ...child, depth, isActiveBranch })
      walk(child.id, depth + 1, isActiveBranch)
    }
  }

  walk(null, 0)
  return rows
})

// Get cell value for a budget line + date
const getCellValue = (budgetLineId: number, date: string): number | null => {
  const lineData = reportData.value[budgetLineId.toString()]
  if (!lineData) return null
  if (lineData[date] === undefined) return null
  return lineData[date]
}

// Delta vs previous column (null for first column or when either value missing)
const getCellDelta = (budgetLineId: number, colIndex: number): number | null => {
  if (colIndex === 0) return null
  const cols = columns.value
  const prevDate = cols[colIndex - 1]?.date
  const currDate = cols[colIndex]?.date
  if (!prevDate || !currDate) return null
  const prev = getCellValue(budgetLineId, prevDate)
  const curr = getCellValue(budgetLineId, currDate)
  if (prev === null || curr === null) return null
  return curr - prev
}

// Format number with thousand separators and 2 decimals
const formatNumber = (value: number): string => {
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

// Format delta with + prefix for positive
const formatDelta = (delta: number): string => {
  const s = formatNumber(Math.abs(delta))
  return delta >= 0 ? `+${s}` : `−${s}`
}

// Format column date for subheader
const formatColDate = (dateStr: string): string => {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('default', { month: 'short', day: 'numeric' })
}

const loadReport = async () => {
  updateUrlFromParams()
  loading.value = true
  try {
    const params = new URLSearchParams({ period: selectedPeriod.value })
    if (selectedPeriod.value === 'day') {
      params.set('date_from', dateFrom.value)
      params.set('date_to', dateTo.value)
    } else if (selectedPeriod.value === 'month') {
      // selectedMonth is "2026-02", send as first day of month
      params.set('date', selectedMonth.value + '-01')
    } else {
      // year mode: send Jan 1 of selected year
      params.set('date', selectedYear.value + '-01-01')
    }
    const response = await apiGet(`/api/budget-report?${params}`)
    if (response.ok) {
      const result = await response.json()
      budgetLines.value = result.budget_lines || []
      columns.value = result.columns || []
      reportData.value = result.data || {}
      activeLineId.value = result.active_line_id || 5
    }
  } catch (error) {
    console.error('Load budget report error:', error)
  } finally {
    loading.value = false
  }
}

// Reload when period changes
watch(selectedPeriod, () => {
  loadReport()
})

// Init from URL on mount and when navigating to the page with query
onMounted(() => {
  initFromQuery()
  loadReport()
})

watch(() => route.query, () => {
  initFromQuery()
  loadReport()
}, { deep: true })
</script>

<style scoped>
.budget-report-page {
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
}

/* Header: title + controls in one row */
.budget-report-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 1rem 1.5rem;
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.budget-report-title {
  font-size: 1.5rem;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.02em;
  color: rgba(var(--v-theme-on-surface), 0.9);
}

.budget-report-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 0.75rem 1.25rem;
}

/* Segmented control: single border, rounded, selected = primary tint */
.period-toggle {
  flex-shrink: 0;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 6px;
  overflow: hidden;
  background: rgb(var(--v-theme-surface));
}

.period-toggle :deep(.v-btn) {
  min-width: 4.5rem;
  border: none;
  border-radius: 0;
  box-shadow: none;
  text-transform: uppercase;
  font-size: 0.75rem;
  font-weight: 500;
  letter-spacing: 0.02em;
}

.period-toggle :deep(.v-btn--active) {
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

.period-toggle :deep(.v-btn-group .v-btn.v-btn--divided) {
  border-inline-start: 1px solid rgba(var(--v-border-color), 0.5);
}

.period-toggle :deep(.v-btn-group .v-btn:first-child) {
  border-inline-start: none;
}

/* Date/month/year fields: label above, outlined, consistent with toggle */
.control-field {
  max-width: 11rem;
}

.control-field :deep(.v-field) {
  border-radius: 6px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.control-field :deep(.v-label) {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}

.control-field-year {
  max-width: 6rem;
}

.control-field-year :deep(.v-field) {
  border-radius: 6px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

/* Period description: grey caption, no box */
.period-description {
  font-size: 0.8125rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin-left: 0;
  align-self: center;
}

/* Table container: no card, light surface */
.budget-report-table-wrapper {
  overflow-x: auto;
  position: relative;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  background: rgb(var(--v-theme-surface));
}

.budget-report-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  min-width: 600px;
}

.budget-report-table th,
.budget-report-table td {
  padding: 0.625rem 0.875rem;
  border-bottom: 1px solid rgba(var(--v-border-color), 0.4);
  vertical-align: top;
}

.budget-report-table thead th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  position: sticky;
  top: 0;
  z-index: 2;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: none;
  letter-spacing: 0;
  padding: 0.5rem 0.875rem;
}

.budget-report-table thead th.col-value {
  text-align: right;
}

.budget-report-table thead th .col-label {
  display: block;
  font-variant-numeric: tabular-nums;
}

.budget-report-table thead th .col-date {
  display: block;
  font-size: 0.6875rem;
  font-weight: 500;
  opacity: 0.7;
  margin-top: 0.125rem;
}

.col-value {
  min-width: 100px;
  position: relative;
  z-index: 0;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.cell-value-block {
  white-space: nowrap;
}

.cell-value-block .cell-value {
  display: block;
  font-weight: 500;
  font-size: 0.875rem;
}

.cell-value-block .cell-delta {
  display: block;
  font-size: 0.6875rem;
  opacity: 0.85;
  margin-top: 0.125rem;
}

.cell-value-block .cell-delta.delta-negative {
  color: rgb(var(--v-theme-error));
  font-weight: 500;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 3;
  background: rgb(var(--v-theme-surface));
  min-width: 120px;
  max-width: 160px;
  border-right: 1px solid rgba(var(--v-border-color), 0.35);
  box-shadow: 4px 0 8px -4px rgba(0, 0, 0, 0.06);
}

.budget-report-table thead .sticky-col {
  z-index: 4;
  background: rgba(var(--v-theme-on-surface), 0.04);
}

.col-name {
  text-align: left;
}

.row-icon {
  margin-right: 0.375rem;
  vertical-align: middle;
  opacity: 0.8;
}

.row-name-folder {
  font-weight: 500;
}

/* Row states: left border by active/passive branch */
.row-branch-active .sticky-col {
  border-left: 3px solid rgb(var(--v-theme-success));
}

.row-branch-passive .sticky-col {
  border-left: 3px solid rgb(var(--v-theme-primary));
}

/* Empty state */
.row-empty td {
  border-bottom: none;
}

.empty-state {
  text-align: center;
  padding: 3rem 1.5rem !important;
}

.empty-icon {
  opacity: 0.35;
  margin-bottom: 0.5rem;
}

.empty-text {
  margin: 0;
  font-size: 0.875rem;
  color: rgba(var(--v-theme-on-surface), 0.6);
}
</style>
